import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Lock, ShoppingBag, Star, Truck } from "lucide-react";
import { useCart, PRODUCT } from "../context/CartContext";
import { useShopProduct } from "../lib/useShopProduct";
import { useAuth } from "../context/AuthContext";
import AuthModal from "../components/AuthModal";
import Accordion from "../components/Accordion";
import InsideFormula from "../components/InsideFormula";
import { FAQS, GALLERY, QUALITY, REVIEWS, TIKTOK_SHOP_URL } from "../data/product";
import { Reveal, EASE } from "../components/Reveal";

export default function ProductPage() {
  const { addItem, openCart } = useCart();
  const live = useShopProduct();
  const { user } = useAuth();
  const [img, setImg] = useState(0);
  const [authOpen, setAuthOpen] = useState(false);

  const name = (live?.title || "Luxe Hydrating Cream").split(/[–—|]/)[0].trim();
  const price = live?.variants?.[0]?.price ?? PRODUCT.price;
  const liveVariantId =
    live?.variants?.find((v) => v.available !== false)?.id || live?.variants?.[0]?.id || null;

  const add = () => {
    addItem({
      id: live?.handle || PRODUCT.id,
      name,
      size: "8 oz",
      price,
      image: "/images/product/jar-front.webp",
      amazonUrl: PRODUCT.amazonUrl,
      variantId: liveVariantId,
    });
    openCart();
  };

  return (
    <div data-testid="product-page" className="bg-[#F8F5F2]">
      {/* GALLERY + BUY BOX */}
      <section className="pt-28 md:pt-36 pb-16 md:pb-24 px-6 md:px-10">
        <div className="mx-auto max-w-[1240px] grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <div className="relative rounded-[26px] overflow-hidden aspect-square">
              <motion.img
                key={img}
                data-testid="pdp-main-image"
                src={GALLERY[img].src}
                alt={GALLERY[img].alt}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: EASE }}
                className={`w-full h-full ${
                  GALLERY[img].contain ? "object-contain p-8" : "object-cover"
                }`}
                draggable={false}
              />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {GALLERY.map((g, i) => (
                <button
                  key={g.src}
                  data-testid={`pdp-thumb-${i}`}
                  onClick={() => setImg(i)}
                  aria-label={`View image ${i + 1}`}
                  className={`aspect-square rounded-2xl overflow-hidden border bg-[#FDFCF9]/60 transition-all duration-500 ${
                    img === i ? "border-lavdeep ring-1 ring-lavdeep" : "border-ink/8 hover:border-ink/30 opacity-80 hover:opacity-100"
                  }`}
                >
                  <img
                    src={g.src}
                    alt=""
                    className={`w-full h-full ${g.contain ? "object-contain p-1.5" : "object-cover"}`}
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:sticky lg:top-28">
            <Reveal>
              <p className="eyebrow">Vivoré Skincare — 8 oz</p>
              <h1 data-testid="pdp-title" className="font-display text-4xl md:text-5xl text-ink mt-3 leading-[1.05]">
                {name}
              </h1>
              <div className="mt-4 flex items-center gap-2.5" data-testid="pdp-rating">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={13} className="fill-ink text-ink" />
                  ))}
                </div>
                <span className="font-sans text-[12px] text-stone">5.0 · Loved by customers</span>
              </div>
              <p className="mt-5 font-light text-[15px] leading-relaxed text-ink/75 max-w-[46ch]">
                A rich body moisturizer designed to hydrate dry skin, support the moisture
                barrier, and leave skin feeling softer and smoother. Lightly scented with
                mango, peach, and apricot.
              </p>

              <div className="mt-7 flex items-center gap-4 flex-wrap">
                <span data-testid="pdp-price" className="font-display text-4xl text-ink">
                  ${price.toFixed(2)}
                </span>
                <span className="font-sans text-[12px] text-stone">($7.50 / oz)</span>
                <span
                  data-testid="free-shipping-badge"
                  className="flex items-center gap-1.5 bg-lavsoft text-lavdeep font-sans text-[10px] uppercase tracking-widest2 px-4 py-2 rounded-full"
                >
                  <Truck size={12} strokeWidth={1.8} /> Free Shipping
                </span>
              </div>

              <div className="mt-7 space-y-3">
                <button
                  data-testid="pdp-add-to-cart"
                  onClick={add}
                  className="btn-lux w-full bg-ink text-ivory text-xs uppercase tracking-widest2 py-4.5 py-4 rounded-full flex items-center justify-center gap-2.5"
                >
                  Add to Cart <ShoppingBag size={14} strokeWidth={1.5} />
                </button>
                <a
                  data-testid="pdp-amazon-button"
                  href={PRODUCT.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-lux w-full border border-ink/35 text-ink text-xs uppercase tracking-widest2 py-4 rounded-full flex items-center justify-center gap-2 hover:bg-ink hover:text-ivory transition-all duration-500"
                >
                  Buy on Amazon <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
                <a
                  data-testid="pdp-tiktok-link"
                  href={TIKTOK_SHOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center font-sans text-[12px] text-stone hover:text-ink transition-colors duration-500 pt-1"
                >
                  Also available on TikTok Shop <ArrowUpRight size={11} strokeWidth={1.5} className="inline" />
                </a>
              </div>

              <div data-testid="quality-badges" className="mt-7 grid grid-cols-3 gap-3">
                {QUALITY.map((q) => (
                  <div
                    key={q.label}
                    className="bg-[#FDFCF9] border border-ink/8 rounded-2xl px-3 py-4 flex flex-col items-center text-center gap-2"
                  >
                    <q.icon size={16} strokeWidth={1.6} className="text-lavdeep" />
                    <span className="font-sans text-[10px] uppercase tracking-wide2 text-ink/75 leading-tight">
                      {q.label}
                    </span>
                  </div>
                ))}
              </div>

              {user === false && (
                <button
                  data-testid="pdp-member-offer"
                  onClick={() => setAuthOpen(true)}
                  className="mt-6 w-full text-left font-sans text-[12px] text-stone hover:text-ink transition-colors duration-500"
                >
                  Join the VIVORÉ list for <span className="text-lavdeep">10% off your first order</span> —
                  create your account
                </button>
              )}
              {user && (
                <p className="mt-6 font-sans text-[12px] text-stone">
                  Your member code <span className="text-lavdeep tracking-[0.15em]">WELCOME10</span> is
                  ready in <Link to="/account" className="underline underline-offset-2 hover:text-ink">your account</Link>.
                </p>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      {/* BENEFITS + KEY INGREDIENTS + FULL LIST */}
      <InsideFormula />

      {/* REVIEWS */}
      <section className="bg-[#FDFCF9] border-y border-ink/8 py-20 md:py-24 px-6 md:px-10">
        <div className="mx-auto max-w-[1000px]">
          <Reveal>
            <div className="flex flex-col items-center">
              <div className="flex gap-1" aria-label="5 out of 5 stars">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} size={15} className="fill-ink text-ink" />
                ))}
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-ink mt-3">5.0 — what customers say</h2>
              <p className="mt-2 font-light text-[13px] text-stone">Genuine reviews from verified customers.</p>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {REVIEWS.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.12}>
                <figure
                  data-testid={`pdp-review-${i}`}
                  className="h-full bg-[#F8F5F2] border border-ink/8 rounded-[24px] p-8"
                >
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={12} className="fill-ink text-ink" />
                    ))}
                  </div>
                  <figcaption className="mt-4 font-sans text-[14px] text-ink">{r.name}</figcaption>
                  <blockquote className="mt-2 font-sans italic font-light text-[15px] leading-relaxed text-ink/75">
                    “{r.text}”
                  </blockquote>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-24 px-6 md:px-10">
        <div className="mx-auto max-w-[860px]">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl text-ink text-center">Your questions</h2>
            <p className="mt-3 font-light text-[13px] text-stone text-center">
              Still curious? Email <a href="mailto:info@vivore.com" className="underline underline-offset-2 hover:text-ink">info@vivore.com</a>
            </p>
            <div className="mt-10 bg-[#FDFCF9] border border-ink/8 rounded-[24px] px-7 md:px-9">
              <Accordion items={FAQS} testPrefix="faq" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink text-ivory py-20 md:py-24 px-6 text-center overflow-hidden">
        <Reveal>
          <p className="eyebrow !text-ivory/50">Free shipping · 10% off your first order with WELCOME10</p>
          <h2 className="font-display text-4xl md:text-6xl mt-4">Begin your ritual.</h2>
          <button
            data-testid="pdp-footer-cta"
            onClick={add}
            className="btn-lux mt-9 bg-ivory text-ink text-xs uppercase tracking-widest2 px-10 py-4 rounded-full inline-flex items-center gap-2.5"
          >
            Add to Cart — ${price.toFixed(2)} <Lock size={12} strokeWidth={1.5} />
          </button>
        </Reveal>
      </section>

      {/* STICKY MOBILE BUY BAR */}
      <div
        data-testid="pdp-sticky-buy-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-[55] bg-[#FDFCF9]/95 backdrop-blur border-t border-ink/10 px-5 py-3 flex items-center justify-between gap-4"
      >
        <div>
          <p className="font-display text-xl text-ink leading-none" data-testid="sticky-price">
            ${price.toFixed(2)}
          </p>
          <p className="font-sans text-[9.5px] uppercase tracking-widest2 text-lavdeep mt-1">
            Free shipping
          </p>
        </div>
        <button
          data-testid="sticky-add-to-cart"
          onClick={add}
          className="btn-lux bg-ink text-ivory text-[11px] uppercase tracking-widest2 px-7 py-3.5 rounded-full flex items-center gap-2"
        >
          Add to Cart <ShoppingBag size={13} strokeWidth={1.5} />
        </button>
      </div>
      <div className="lg:hidden h-20" />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialTab="register" />
    </div>
  );
}
